// Free Shipping + Free Gifts Bar
var FreeShippingBar = class extends HTMLElement {
  static get observedAttributes() {
    return ["threshold", "total-price", "first-gift-threshold", "second-gift-threshold"];
  }

  constructor() {
    super();
    this._onCartChangedListener = this._onCartChanged.bind(this);
    this._currencyFormatter = new Intl.NumberFormat(Shopify.locale, {
      style: "currency",
      currency: Shopify.currency.active
    });
    this.totalPrice = 0;
  }

  async connectedCallback() {
    // Normalize thresholds to current currency
    this.threshold = Math.round(this.getAttribute("threshold") * (Shopify.currency.rate || 1));
    this.firstGiftThreshold = Math.round(this.getAttribute("first-gift-threshold") * (Shopify.currency.rate || 1));
    this.secondGiftThreshold = Math.round(this.getAttribute("second-gift-threshold") * (Shopify.currency.rate || 1));

    document.addEventListener("theme:cart:update", this._onCartChangedListener);
    document.addEventListener("theme:cart:change", this._onCartChangedListener);

    // Fetch initial cart data on load
    await this.fetchAndUpdateCart();

    this._updateMessage();
  }

  disconnectedCallback() {
    // FIX: Changed from addEventListener to removeEventListener
    document.removeEventListener("theme:cart:update", this._onCartChangedListener);
    document.removeEventListener("theme:cart:change", this._onCartChangedListener);
  }

  get threshold() {
    return parseFloat(this.getAttribute("threshold"));
  }
  set threshold(value) {
    this.setAttribute("threshold", value);
  }

  get firstGiftThreshold() {
    return parseFloat(this.getAttribute("first-gift-threshold"));
  }
  set firstGiftThreshold(value) {
    this.setAttribute("first-gift-threshold", value);
  }

  get secondGiftThreshold() {
    return parseFloat(this.getAttribute("second-gift-threshold"));
  }
  set secondGiftThreshold(value) {
    this.setAttribute("second-gift-threshold", value);
  }

  get totalPrice() {
    return parseFloat(this.getAttribute("total-price"));
  }
  set totalPrice(value) {
    this.setAttribute("total-price", value);
  }

  async attributeChangedCallback(attribute, oldValue, newValue) {
    await window.customElements.whenDefined("progress-bar");
    const progressBarElement = this.querySelector("progress-bar");

    if (attribute === "threshold") {
      progressBarElement.valueMax = this.secondGiftThreshold;
    }

    if (attribute === "total-price") {
      const numericValue = parseFloat(newValue) || 0;
      progressBarElement.setAttribute("aria-valuenow", numericValue);

      const valMax = parseFloat(progressBarElement.getAttribute("aria-valuemax")) || this.secondGiftThreshold || this.threshold;
      const ratio = valMax > 0 ? Math.min(1, numericValue / valMax) : 0;
      progressBarElement.style.setProperty("--progress", ratio);
      
      // FIX: Ensure messages and markers update after progress bar
      this._updateMessage();
    }
  }

  async fetchAndUpdateCart() {
    try {
      // FIX: Fetch cart directly from Shopify API
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      this._processCartData(cartData);
      this._updateMessage();
    } catch (error) {
      console.error("Error fetching cart data:", error);
      this._updateMessage();
    }
  }

  _processCartData(cart) {
    const priceForItems = cart.items
      .reduce((sum, item) => sum + item.final_line_price, 0);

    const cartDiscount = cart.cart_level_discount_applications && cart.cart_level_discount_applications.length
      ? cart.cart_level_discount_applications.reduce((sum, discountAllocation) => sum + discountAllocation.total_allocated_amount, 0)
      : 0;

    this.totalPrice = priceForItems - cartDiscount;
    
    // FIX: Update progress bar and message together
    const progressBarElement = this.querySelector('progress-bar');
    if (progressBarElement) {
      progressBarElement.valueNow = this.totalPrice;
    }
  }

  _updateMessage() {
    const currency = this._currencyFormatter;
    const msgEl = this.querySelector(".progress-message");

    const currentThreshold = parseFloat(this.threshold);
    const currentFirstGiftThreshold = parseFloat(this.firstGiftThreshold);
    const currentSecondGiftThreshold = parseFloat(this.secondGiftThreshold);
    const currentTotalPrice = parseFloat(this.totalPrice);

    const remainingShipping = currentThreshold - currentTotalPrice;
    const remainingFirstGift = currentFirstGiftThreshold - currentTotalPrice;
    const remainingSecondGift = currentSecondGiftThreshold - currentTotalPrice;

    const replacements = {
      "{shipping}": currency.format(Math.max(0, remainingShipping) / 100),
      "{gift1}": currency.format(Math.max(0, remainingFirstGift) / 100),
      "{gift2}": currency.format(Math.max(0, remainingSecondGift) / 100)
    };

    let message = "";

    if (currentTotalPrice >= currentSecondGiftThreshold) {
      message = this.getAttribute("msg-gift2");
    } else if (currentTotalPrice >= currentFirstGiftThreshold) {
      message = this.getAttribute("msg-gift1");
    } else if (currentTotalPrice >= currentThreshold) {
      message = this.getAttribute("msg-shipping-unlocked");
    } else {
      message = this.getAttribute("msg-below-shipping");
    }

    Object.keys(replacements).forEach(key => {
      message = message.replaceAll(key, replacements[key]);
    });

    if (msgEl) {
      msgEl.innerHTML = message;
    }
    
    this._updateMarkers();
  }

  _updateMarkers() {
    const shippingMarker = this.querySelector(".shipping-marker");
    const gift1Marker = this.querySelector(".gift1-marker");
    const gift2Marker = this.querySelector(".gift2-marker");
    
    const currentTotalPrice = parseFloat(this.totalPrice) || 0;
    
    // FIX: Compare against actual threshold values, not percentages
    if (shippingMarker) {
      if (currentTotalPrice >= this.threshold) {
        shippingMarker.classList.add("reached");
      } else {
        shippingMarker.classList.remove("reached");
      }
    }
    
    if (gift1Marker) {
      if (currentTotalPrice >= this.firstGiftThreshold) {
        gift1Marker.classList.add("reached");
      } else {
        gift1Marker.classList.remove("reached");
      }
    }
    
    if (gift2Marker) {
      if (currentTotalPrice >= this.secondGiftThreshold) {
        gift2Marker.classList.add("reached");
      } else {
        gift2Marker.classList.remove("reached");
      }
    }
  }

  async _onCartChanged(event) {
    // FIX: Always fetch fresh cart data to ensure accuracy
    await this.fetchAndUpdateCart();
  }
};

if (!window.customElements.get("free-shipping-bar")) {
  window.customElements.define("free-shipping-bar", FreeShippingBar);
}

// Progress bar component
var ProgressBar = class extends HTMLElement {
  static get observedAttributes() {
    return ["aria-valuenow", "aria-valuemax"];
  }
  
  set valueMax(value) {
    this.setAttribute("aria-valuemax", value);
  }
  
  set valueNow(value) {
    this.setAttribute("aria-valuenow", value);
  }
  
  attributeChangedCallback() {
    const valNow = parseFloat(this.getAttribute("aria-valuenow"));
    const valMax = parseFloat(this.getAttribute("aria-valuemax"));

    if (!isNaN(valNow) && !isNaN(valMax) && valMax > 0) {
      this.style.setProperty(
        "--progress",
        `${Math.min(1, valNow / valMax)}`
      );
    } else {
      this.style.setProperty("--progress", "0");
    }
  }
};

if (!window.customElements.get("progress-bar")) {
  window.customElements.define("progress-bar", ProgressBar);
}